// src/hooks/useAuth.ts
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { User } from 'oidc-client-ts';
import { signOutRedirect, hasAdminRole } from '../utils/auth';

/**
 * Type guard to check if roles is an array of strings
 */
const isRolesArray = (roles: any): roles is string[] => {
  return Array.isArray(roles);
}

/**
 * Enhanced auth interface with additional helper methods
 */
export interface EnhancedAuth {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  error: Error | null;
  signIn: () => void;
  signOut: () => Promise<void>;
  getUserName: () => string;
  getUserEmail: () => string | undefined;
  getUserInitial: () => string;
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
}

/**
 * Custom hook to handle all authentication-related functionality
 * This encapsulates OIDC logic and provides a simpler interface
 */
export const useAuth = (): EnhancedAuth => {
  const oidcAuth = useOidcAuth();
  
  /**
   * Override the signOut function to use our custom signOutRedirect
   * instead of the OIDC library's signoutRedirect
   */
  const signOut = async () => {
    try {
      console.log("useAuth: Signing out user");
      
      // Use our custom utility function instead of OIDC's signoutRedirect
      signOutRedirect();
      
      return Promise.resolve();
    } catch (error) {
      console.error("Error in signOut:", error);
      return Promise.reject(error);
    }
  };

  return {
    // Pass through core auth state
    isAuthenticated: oidcAuth.isAuthenticated,
    isLoading: oidcAuth.isLoading,
    user: oidcAuth.user || null,
    error: oidcAuth.error || null,
    signIn: oidcAuth.signinRedirect,
    signOut,
    
    // Helper methods
    getUserName: () => oidcAuth.user?.profile.given_name || oidcAuth.user?.profile.name || 'User',
    getUserEmail: () => oidcAuth.user?.profile.email,
    getUserInitial: () => (oidcAuth.user?.profile.given_name || oidcAuth.user?.profile.name || 'U').charAt(0),
    hasRole: (role: string) => {
      const roles = oidcAuth.user?.profile?.roles;
      return isRolesArray(roles) ? roles.includes(role) : false;
    },
    // New method to check if user is an admin
    isAdmin: () => {
      return oidcAuth.user?.profile ? hasAdminRole(oidcAuth.user.profile) : false;
    }
  };
};

export default useAuth;